// Local storage prefix to avoid conflicts
const PREFIX = 'megalith_maps_';

/**
 * Get value from local storage with JSON parsing and error handling
 */
export const getLocalStorage = (key) => {
  try {
    const storedValue = localStorage.getItem(PREFIX + key);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

/**
 * Set value in local storage with JSON stringification and error handling
 */
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('Error writing to localStorage:', error);
    
    // If we hit quota, try to clear old entries
    if (error.name === 'QuotaExceededError') {
      try {
        clearOldEntries();
        // Try setting the value again
        localStorage.setItem(PREFIX + key, JSON.stringify(value));
        return true;
      } catch (retryError) {
        console.error('Error after clearing storage:', retryError);
        return false;
      }
    }
    return false;
  }
};

/**
 * Remove value from local storage
 */
export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(PREFIX + key);
    return true;
  } catch (error) {
    console.error('Error removing from localStorage:', error);
    return false;
  }
};

/**
 * Clear all Megalith Maps related entries from local storage
 */
export const clearLocalStorage = () => {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(PREFIX))
      .forEach(key => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Clear old entries when storage is full
 * Removes entries older than 7 days
 */
const clearOldEntries = () => {
  const EXPIRY_DAYS = 7;
  const now = Date.now();
  
  Object.keys(localStorage)
    .filter(key => key.startsWith(PREFIX))
    .forEach(key => {
      try {
        const value = JSON.parse(localStorage.getItem(key));
        if (value?._timestamp && (now - value._timestamp > EXPIRY_DAYS * 24 * 60 * 60 * 1000)) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        // If we can't parse the value, remove it
        localStorage.removeItem(key);
      }
    });
};

/**
 * Add timestamp to value before storing
 */
export const setLocalStorageWithExpiry = (key, value) => {
  const valueWithTimestamp = {
    ...value,
    _timestamp: Date.now()
  };
  return setLocalStorage(key, valueWithTimestamp);
};

/**
 * Get value and check if it has expired
 */
export const getLocalStorageWithExpiry = (key, expiryDays = 7) => {
  const value = getLocalStorage(key);
  if (!value || !value._timestamp) return null;

  const now = Date.now();
  if (now - value._timestamp > expiryDays * 24 * 60 * 60 * 1000) {
    removeLocalStorage(key);
    return null;
  }

  const { _timestamp, ...rest } = value;
  return rest;
};
